import type { Collections } from '@nuxt/content'
import type { PersonContact } from '~/interfaces/person'
import type { IThesis } from '~/interfaces/thesis'
import { Thesis } from '~/models/thesis'
import { toPersonContact } from '~/utils/person'

type PersonRecord = Collections['persons']
type ThesisRecord = Collections['theses']

/**
 * Batch-based hydrated theses.
 * Fetches all theses and populates supervisorPerson/tutorPerson
 *  fields with person objects.
 */
export async function getAllTheses() {
  // 1. Fetch all theses
  const thesesData = await queryCollection('theses')
    .order('status', 'ASC')
    .all()

  // 2. Collect all person IDs referenced as supervisor/tutor
  const personIds = Array.from(
    new Set(thesesData.flatMap((t) => [t.supervisor, t.tutor]).filter(Boolean)),
  )

  // 3. Fetch all relevant persons in one go
  const persons = await queryCollection('persons')
    .where('personId', 'IN', personIds)
    .all()

  // 4. Build a lookup map for quick access
  const personMap = new Map<string, PersonContact>(
    persons.map((person: PersonRecord) => [
      person.personId,
      toPersonContact(person),
    ]),
  )

  // 5. Re-attach supervisor/tutor to each thesis
  const theses = thesesData.map((thesis: ThesisRecord) =>
    Thesis.create(
      {
        ...thesis,
        studentName: thesis.studentName ?? '',
      } as IThesis,
      personMap.get(thesis.supervisor),
      personMap.get(thesis.tutor),
    ),
  )
  return theses
}
